import { useEffect, useRef } from 'react'
import { FiChevronRight, FiFilter } from 'react-icons/fi'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { YOUTUBE_CHANNEL_URL } from '@domain/features/youtube/youtube-content'
import noSilverBullet4PDLogo from '@presentation/assets/icons/no-silver-bullet-4pd-logo-icon.svg'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import WebinarCard from '@presentation/components/ui/WebinarCard'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useHomeWebinars } from '@presentation/hooks/useHomeWebinars'
import { useWebinarSuggestions } from '@presentation/hooks/useWebinarSuggestions'
import { useWebinarSuggestionsActions } from '@presentation/hooks/useWebinarSuggestionsActions'
import SuggestedWebinarCard from './SuggestedWebinarCard'
import WebinarSuggestionModal from './WebinarSuggestionModal'

const SUGGESTIONS_PREVIEW_FETCH_LIMIT = 3

export default function WebinarsSection() {
  const location = useLocation()
  const navigate = useNavigate()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { user } = useAuthUser()
  const {
    latestWebinars,
    upcomingWebinars,
    loading,
    error,
  } = useHomeWebinars()
  const {
    suggestions,
    loading: suggestionsLoading,
    error: suggestionsError,
    reload: reloadSuggestions,
  } = useWebinarSuggestions({ limit: SUGGESTIONS_PREVIEW_FETCH_LIMIT })

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const {
    isSuggestionModalOpen,
    editingSuggestion,
    modalSubmitting,
    modalError,
    actionError,
    activeLikeId,
    openCreateSuggestionModal,
    closeSuggestionModal,
    handleSubmitSuggestion,
    handleToggleSuggestionLike,
  } = useWebinarSuggestionsActions({
    user,
    onAuthRequired: openLoginModal,
    onSuggestionsChanged: reloadSuggestions,
  })
  const hasHandledInitialSuggestionsLoad = useRef(false)

  useEffect(() => {
    if (!hasHandledInitialSuggestionsLoad.current) {
      hasHandledInitialSuggestionsLoad.current = true
      return
    }
    reloadSuggestions()
  }, [user?.uid, reloadSuggestions])

  const showInitialLoading =
    loading && latestWebinars.length === 0 && upcomingWebinars.length === 0
  const showBlockingError = error && latestWebinars.length === 0

  return (
    <>
      <section className="box-border bg-blue-light py-4 sm:py-6 lg:py-8">
        <div className="mx-auto flex max-w-7xl flex-col pl-8 pr-4 sm:pl-10 sm:pr-6 lg:pl-14 lg:pr-8">
          <div className="mb-4 w-full max-w-84 pb-6 sm:max-w-92">
            <a
              href={YOUTUBE_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                AnalyticsFeature.trackExternalLinkOpen(
                  screenPath,
                  'webinars_home_logo',
                  'external_link',
                  YOUTUBE_CHANNEL_URL,
                  Boolean(user),
                )
              }}
              className="inline-flex"
            >
              <img
                src={noSilverBullet4PDLogo}
                alt="No Silver Bullet 4PD"
                className="h-auto w-full"
              />
            </a>
          </div>
          <div className="pl-2 sm:pl-4 lg:pl-5">
            <div className="flex shrink-0 items-center justify-between gap-4">
              <Link to="/webinars/latest" className="no-underline">
                <h2 className="text-2xl font-bold tracking-tight text-blue sm:text-3xl">
                  Our Latest Webinars
                </h2>
              </Link>
              <Link
                to="/webinars/latest"
                aria-label="Browse and filter latest Parkinson's webinars"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-blue px-4 py-2.5 text-sm font-semibold text-blue no-underline shadow-sm transition-colors hover:bg-blue/8"
              >
                <FiFilter className="h-4 w-4" aria-hidden="true" />
                Filter
              </Link>
            </div>
            <div className="mt-3 h-0.5 w-full bg-blue" />
          </div>

          {showInitialLoading ? <LoadingSpinner /> : null}

          {showBlockingError ? (
            <p className="mt-6 text-center text-text-secondary">
              Unable to load webinars. Please try again later.
            </p>
          ) : null}

          {!showInitialLoading && !showBlockingError && (
            <>
              {latestWebinars.length === 0 ? (
                <p className="mt-6 text-sm text-text-muted">
                  No completed webinars available yet.
                </p>
              ) : (
                <div className="mt-8 grid grid-cols-1 gap-6 pt-1 sm:grid-cols-2 lg:grid-cols-4">
                  {latestWebinars.map((webinar, index) => (
                    <div
                      key={webinar.uid}
                      className={index >= 2 ? 'hidden sm:block' : undefined}
                    >
                      <WebinarCard webinar={webinar} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="mt-4 flex shrink-0 justify-center sm:mt-6">
            <Link
              to="/webinars/latest"
              aria-label="See more latest Parkinson's webinars"
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue px-4 py-3 text-center text-sm font-semibold leading-tight text-white no-underline shadow-sm transition-[transform,opacity,background-color] duration-200 hover:scale-[1.02] hover:bg-blue/90 hover:opacity-90 sm:text-base"
            >
              See more latest webinars
              <FiChevronRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {upcomingWebinars.length > 0 ? (
        <section className="box-border bg-blue-light py-4 sm:py-6 lg:py-8">
          <div className="mx-auto flex max-w-7xl flex-col pl-8 pr-4 sm:pl-10 sm:pr-6 lg:pl-14 lg:pr-8">
            <div className="pl-2 sm:pl-4 lg:pl-5">
              <div className="flex shrink-0 items-center justify-between gap-4">
                <Link to="/webinars/upcoming" className="no-underline">
                  <h2 className="text-2xl font-bold tracking-tight text-blue sm:text-3xl">
                    Upcoming Webinars
                  </h2>
                </Link>
                <Link
                  to="/webinars/upcoming"
                  aria-label="Browse and filter upcoming Parkinson's webinars"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-blue px-4 py-2.5 text-sm font-semibold text-blue no-underline shadow-sm transition-colors hover:bg-blue/8"
                >
                  <FiFilter className="h-4 w-4" aria-hidden="true" />
                  Filter
                </Link>
              </div>
              <div className="mt-3 h-0.5 w-full bg-blue" />
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 pt-1 sm:grid-cols-2 lg:grid-cols-3">
              {upcomingWebinars.map((webinar) => (
                <WebinarCard
                  key={webinar.uid}
                  webinar={webinar}
                  showVideoPreview={false}
                />
              ))}
              <SuggestedWebinarCard
                user={user}
                suggestions={suggestions}
                loading={suggestionsLoading}
                error={suggestionsError}
                actionError={actionError}
                activeLikeId={activeLikeId}
                onCreate={openCreateSuggestionModal}
                onToggleLike={(suggestion) => {
                  void handleToggleSuggestionLike(
                    suggestion.uid,
                    suggestion.submittedByUid,
                    suggestion.subject,
                  )
                }}
              />
            </div>
          </div>
        </section>
      ) : null}

      <WebinarSuggestionModal
        key={`${editingSuggestion?.uid ?? 'create'}-${isSuggestionModalOpen ? 'open' : 'closed'}`}
        isOpen={isSuggestionModalOpen}
        mode={editingSuggestion ? 'edit' : 'create'}
        initialValue={
          editingSuggestion
            ? {
                subject: editingSuggestion.subject,
                description: editingSuggestion.description,
              }
            : undefined
        }
        isSubmitting={modalSubmitting}
        error={modalError}
        onSubmit={(input) => {
          void handleSubmitSuggestion(input)
        }}
        onClose={closeSuggestionModal}
      />
    </>
  )
}
